import { KgInsight, KgInsightCategory } from '@api';
import { getEntityTagFromSnake, getTickersFromSnakes } from '@helpers';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { EntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import { MarketReactionSection } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-recap/components/market-reaction-section/MarketReactionSection';
import { CompanyCatalystReaction } from '../company-catalyst-reaction/CompanyCatalystReaction';
import { EarningsPreviewReaction } from '../earnings-preview-reaction/EarningsPreviewReaction';
import { MarketCatalystReaction } from '../market-catalyst-reaction/MarketCatalystReaction';
import styles from './CatalystReaction.module.scss';

export interface CatalystReactionProps {
  kgInsight: KgInsight;
  category: KgInsightCategory;
}

export const CatalystReaction = ({
  kgInsight,
  category,
}: CatalystReactionProps) => {
  const heroSection = kgInsight.sections.find(
    section => section.type === 'hero'
  );
  const snakes = heroSection?.data.snakes ?? [];
  const entityTags = snakes.map(getEntityTagFromSnake);

  const { data: entityData } = useEntity(
    // if the insights subject is an entity, use the subject id
    // otherwise its market catalyst and we use the first entity tag from the snakes in the hero section
    kgInsight.subject.type === 'entity' ? kgInsight.subject.id : entityTags[0]
  );
  const entity = entityData?.data;

  const leftHeaderNode = entity ? (
    <div className={styles.watchlistRow}>
      <EntityHeaderWatchlist entity={entity} />
    </div>
  ) : null;

  const companyCatalystReactionElement = (
    <CompanyCatalystReaction
      entityTag={kgInsight.subject.id}
      entity={entity}
      reportingDate={kgInsight.created_at}
      isMultipleEntitiesChart={false}
      leftHeaderNode={leftHeaderNode}
    />
  );

  const mapCategoryToReaction = {
    market_catalyst: (
      <MarketCatalystReaction
        createdAt={kgInsight.created_at}
        tags={entityTags}
        tickers={getTickersFromSnakes(snakes)}
        themeId={
          kgInsight.subject.type === 'theme' ? kgInsight.subject.id : undefined
        }
        leftHeaderNode={leftHeaderNode}
      />
    ),
    company_catalyst: companyCatalystReactionElement,
    earnings_preview: (
      <EarningsPreviewReaction
        kgInsight={kgInsight}
        earningsId={kgInsight.subject.ref?.id}
      />
    ),
    earnings_recap: (
      <MarketReactionSection
        entity={entity!}
        earningsId={kgInsight.subject.ref?.id ?? ''}
        reportingDate={kgInsight.created_at}
      />
    ),
  };

  return (
    <ErrorBoundaryWrapper>
      {mapCategoryToReaction[category]}
    </ErrorBoundaryWrapper>
  );
};
